import { i18n } from "@lingui/core";
import { t } from "@lingui/core/macro";
import { assertNever, ComparatorResult, EMPTY_ARRAY, EMPTY_STRING, formatNumber } from "@regrello/core-utils";
import { DataTestIds } from "@regrello/data-test-ids-api";
import type {
  ConditionOperator,
  CreateFieldInstanceValueInputs,
  FieldFields,
  FieldInstanceFields,
  FieldInstanceFieldsWithBaseValues,
  FieldInstanceValueFieldsFieldInstanceValueFloat,
  FieldInstanceValueFieldsFieldInstanceValueInt,
  FieldInstanceValueInputType,
  PropertyDataType,
  PropertyTypeFields,
  SpectrumFieldVersionFields,
  TableFieldInstanceFields,
  UpdateFieldInstanceValueInputs,
  UpdateStartingConditionsInputs,
  ViewFilterFields,
} from "@regrello/graphql-api";
import { RegrelloIcon } from "@regrello/ui-core";
import type { FieldPath, FieldValues } from "react-hook-form";

import { ValidationRules } from "../../../../../constants/globalConstants";
import type { FieldInstanceBaseFields } from "../../../../../types";
import { numberComparator } from "../../../../../utils/comparators/numberComparator";
import { getFieldInstanceId } from "../../../../../utils/customFields/getFieldInstanceId";
import {
  getRegrelloDefaultFilterDefinitionNumberValue,
  getRegrelloFilterDefinitionNumberValue,
} from "../../../../../utils/filters/v2/filterKinds/core/filterNumber";
import { mergeReactHookFormValidators } from "../../../../../utils/formFields/mergeReactHookFormValidators";
import { getErrorMessageWithPayload } from "../../../../../utils/getErrorMessageWithPayload";
import { TableCellDefaultWidths } from "../../../../../utils/tableCellWidthUtils";
import { RegrelloControlledFormFieldNumber } from "../../formFields/controlled/RegrelloControlledFormFieldNumber";
import {
  getDecimalPlacesFromFieldVersion,
  getDisallowGroupingsFloatFromFieldVersion,
} from "../../spectrumFields/utils/spectrumFieldConstraintUtils";
import { RegrelloCustomFieldMultiValuePopover } from "../components/RegrelloCustomFieldMultiValuePopover";
import { CustomFieldPluginRegistrar } from "./registry/customFieldPluginRegistrar";
import type {
  CustomFieldPlugin,
  CustomFieldPluginV2RenderFormFieldProps,
  GetCreateFieldInstanceValueInputsFromFormValueParams,
  RenderDisplayValueOptions,
} from "./types/CustomFieldPlugin";
import { createViewColumnsFromField } from "./utils/createViewColumnsFromField";
import { DEFAULT_INPUT_TYPE_IF_NO_VALUE, getConditionOperatorsByType } from "./utils/customFieldConstants";
import { extractAtMostOneTabularArrayOrThrow } from "./utils/extractAtMostOneTabularArrayOrThrow";
import { extractAtMostOneValueOrThrow } from "./utils/extractAtMostOneValueOrThrow";
import {
  getIsFieldInstanceFields,
  getIsFieldInstanceValueWithCrossWorkflowFields,
} from "./utils/fieldInstanceTypeguards";
import { getUpdateStartingConditionsInputsForEmptyOperators } from "./utils/getUpdateStartingConditionsInputsForEmptyOperators";
import { validateRequired } from "./utils/validateBulkUploadCellValueUtils";

// TODO Misc: Turn the following into warnings and handle gracefully so the app doesn't crash in
// case of field misconfiguration.
const ERROR_INVALID_FIELD = "Provided 'number' field is invalid";
const WARNING_INVALID_FORM_VALUE = "Provided 'number'-field form value is not a valid number or number string";
const ERROR_INVALID_VALUE_COUNT = "Provided 'number' field instance cannot have multiple values";
const ERROR_INVALID_VALUE_TYPE = "Provided 'number' field instance value must have type 'FieldInstanceValueInt'";
const WARNING_INVALID_OPERATOR_TYPE = "Provided operator type is invalid for 'number' fields";
const WARNING_UNEXPECTED_DEFINED_FORM_VALUE =
  "Provided 'number' field form value must not be defined given the provided operator type";

/**
 * Number fields should show 0 decimal places by default.
 */
const DEFAULT_DECIMAL_PLACES = 0;

function canProcessPropertyDataType(propertyDataType: PropertyDataType): boolean {
  // (clewis): Legacy number fields are INT-backed, but now they are FLOAT-backed so that they can
  // support decimal places. Unless or until we migrate all legacy number fields to FLOAT-backed,
  // we need to support both types.
  return propertyDataType === "FLOAT" || propertyDataType === "INT";
}

function canProcessField(field: FieldFields): boolean {
  return (
    !field.isMultiValued &&
    field.allowedValues.length === 0 &&
    canProcessPropertyDataType(field.propertyType.dataType) &&
    field.fieldUnit == null
  );
}

type NumberFieldPluginType = CustomFieldPlugin<number | null>;

const getConditionOperators: NumberFieldPluginType["getConditionOperators"] = () =>
  getConditionOperatorsByType("NUMBER");

function renderDisplayValueInternal(fieldVersion: SpectrumFieldVersionFields, value: number | null) {
  if (value == null) {
    return null;
  }

  const numberValue = Number(value);
  if (!isFiniteNumber(numberValue)) {
    return value;
  }

  const decimalPlaces = getDecimalPlacesFromFieldVersion(fieldVersion) ?? DEFAULT_DECIMAL_PLACES;
  const disallowGroupings = getDisallowGroupingsFloatFromFieldVersion(fieldVersion) ?? false;

  return formatNumber(numberValue, i18n.locale, { decimalPlaces, disallowGroupings });
}

function renderDisplayValueFromStringValue(
  fieldVersion: SpectrumFieldVersionFields,
  value: string,
  _options?: RenderDisplayValueOptions,
) {
  return renderDisplayValueInternal(fieldVersion, Number(value));
}

const renderDisplayValue: NumberFieldPluginType["renderDisplayValue"] = (fieldInstance) => {
  const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
  return renderDisplayValueInternal(fieldInstance.spectrumFieldVersion, frontendFieldInstance.value);
};

const sortComparator: NumberFieldPluginType["sortComparator"] = (
  fieldInstance1,
  fieldInstance2,
  direction = "asc",
): ComparatorResult => {
  if (direction === "desc") {
    return NumberFieldPlugin.sortComparator(fieldInstance2, fieldInstance1, "asc");
  }

  if (fieldInstance1 == null) {
    return ComparatorResult.BEFORE;
  }

  if (fieldInstance2 == null) {
    return ComparatorResult.AFTER;
  }

  const fieldPlugin = CustomFieldPluginRegistrar.getPluginForField(fieldInstance1.field);

  const value1 = Number(fieldPlugin.getValueForFrontend(fieldInstance1));
  const value2 = Number(fieldPlugin.getValueForFrontend(fieldInstance2));

  return numberComparator(value1, value2);
};

/**
 * Describes a custom field that holds a number value (whether an integer or a float).
 */
// Developer's Note (clewis): Number fields used to be backed by an Int value; now they're backed by
// a Float value so that we can support decimal places. Despite this new scheme, there are still
// many pre-existing number fields in the database. Migrating those is prohibitive right now because
// there may still be API consumers who expect an Int value. Thus, for now we support reading and
// editing both backing types, but we only support creating Float values.
export const NumberFieldPlugin: NumberFieldPluginType = {
  uri: "com.regrello.customField.number",
  version: "1.0.0",

  canProcessField: (field: FieldFields): boolean => {
    return canProcessField(field);
  },

  canProcessFieldInstance: (fieldInstance: FieldInstanceBaseFields): boolean => {
    try {
      translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
      return true;
    } catch (_error) {
      return false;
    }
  },

  canProcessPropertyDataType,

  findPropertyTypeIdFromLoadedPropertyTypeIds: (propertyTypes: PropertyTypeFields[]): number | undefined => {
    return propertyTypes.find((propertyType) => propertyType.dataType === "INT")?.id;
  },

  getColumnsForTable: createViewColumnsFromField,

  getConditionOperators,

  getCreateFieldInstanceValueInputsFromFormValue: (
    inputs: GetCreateFieldInstanceValueInputsFromFormValueParams,
  ): CreateFieldInstanceValueInputs => {
    const { displayOrder, field, inputType, displayContext, sourceFieldInstanceId, spectrumFieldVersion, value } =
      inputs;
    const isValueValid = value == null || isFiniteNumber(value);
    if (!isValueValid) {
      throw new Error(getErrorMessageWithPayload(WARNING_INVALID_FORM_VALUE, { field, inputType, value }));
    }
    const baseProps = {
      fieldId: field.id,
      inputType,
      displayContext: displayContext ?? "DEFAULT",
      displayOrder,
      spectrumFieldVersionId: spectrumFieldVersion.id,
      sourceFieldInstanceValueId: sourceFieldInstanceId,
    };

    // Pass both types of values since we don't know whether the field is INT-backed (legacy) or
    // FLOAT-backed.
    return {
      ...baseProps,
      floatValue: isFiniteNumber(value) ? value : undefined,
      intValue: isFiniteNumber(value) ? Math.trunc(value) : undefined,
    };
  },

  getCrossWorkflowSinksFieldInstanceIds: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): number[] => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.crossWorkflowSinksFieldInstanceIds;
  },

  getCrossWorkflowSourceFieldInstanceIdFromValue: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): number | undefined => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.crossWorkflowSourceFieldInstanceId;
  },

  getFieldDisplayName: (): string => {
    return t`Number`;
  },

  getFilterDefinition: (_field: FieldFields) => {
    return getRegrelloDefaultFilterDefinitionNumberValue();
  },

  getFilterDefinitionWithValues: (_field: FieldFields, filter: ViewFilterFields) => {
    if (filter.value == null) {
      return undefined;
    }
    const value = Number.parseFloat(filter.value);
    return getRegrelloFilterDefinitionNumberValue(filter.operator, value);
  },

  getEmptyValueForFrontend: (_, defaultValue) => {
    if (defaultValue == null || defaultValue === EMPTY_STRING) {
      return null;
    }

    // Treat the value as a float. (If the value is a legacy INT, then it will be parsed as such.)
    const defaultValueAsNumber = Number.parseFloat(defaultValue);
    return isFiniteNumber(defaultValueAsNumber) ? defaultValueAsNumber : null;
  },

  getEmptyTabularValueForFrontend: (rowNumber: number): { value: number | null; rowNumber: number } => {
    return { value: null, rowNumber };
  },

  getIconName: () => {
    return "number-field";
  },

  getNameTemplateDisplayValueFromFormValue: (value, fieldInstance, _options) => {
    if (value == null || !isFiniteNumber(value)) {
      return undefined;
    }
    return renderDisplayValueInternal(fieldInstance.spectrumFieldVersion, value);
  },

  getPreferredHomeTableColumnWidth: () => {
    return TableCellDefaultWidths.NUMBER_CELL;
  },

  getSourceFieldInstance: (_fieldInstance: FieldInstanceFields): FieldInstanceFields | undefined => {
    return undefined;
  },

  getSourceFieldInstanceId: (fieldInstance: FieldInstanceBaseFields) => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.sourceFieldInstanceId;
  },

  getSinkFieldInstanceIds: (fieldInstance: FieldInstanceFields | FieldInstanceBaseFields) => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.sinksFieldInstanceIds;
  },

  getSourceFieldInstanceInputType: (
    fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
  ): FieldInstanceValueInputType | undefined => {
    const sourceValue = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return sourceValue.sourceFieldInstanceInputType;
  },

  getUpdateStartingConditionsInputsFromFormValues: (
    leftFieldInstance: FieldInstanceFields,
    value: unknown,
    operator: ConditionOperator,
  ): UpdateStartingConditionsInputs | undefined => {
    if (!isOperatorValid(operator)) {
      console.warn(WARNING_INVALID_OPERATOR_TYPE, {
        leftFieldInstance,
        operator,
      });
      return undefined;
    }

    if (operator === "EMPTY" || operator === "NOT_EMPTY") {
      if (value != null) {
        console.warn(WARNING_UNEXPECTED_DEFINED_FORM_VALUE, {
          leftFieldInstance,
          value,
        });
      }
      return getUpdateStartingConditionsInputsForEmptyOperators(getFieldInstanceId(leftFieldInstance), operator);
    }

    // Support both legacy int-valued number fields and new float-valued number fields.

    if (operator === "BETWEEN") {
      // (zstanik): For the `BETWEEN` operator, the value is expected to be an array of 2
      // stringified numbers: a lower bound and upper bound.
      if (!Array.isArray(value) || value.length !== 2 || value.some((valueItem) => !isFiniteNumber(valueItem))) {
        console.warn(WARNING_INVALID_FORM_VALUE, { leftFieldInstance, value });
        return undefined;
      }

      switch (leftFieldInstance.field.propertyType.dataType) {
        case "FLOAT": {
          return {
            leftFieldInstanceValueID: getFieldInstanceId(leftFieldInstance),
            operatorV2: operator,
            rightIntMultiValue: EMPTY_ARRAY,
            rightFloatMultiValue: value,
            rightPartyIDMultiValue: EMPTY_ARRAY,
            rightStringMultiValue: EMPTY_ARRAY,
            rightTimeMultiValue: EMPTY_ARRAY,
            rightDateMultiValue: EMPTY_ARRAY,
          };
        }
        case "INT": {
          return {
            leftFieldInstanceValueID: getFieldInstanceId(leftFieldInstance),
            operatorV2: operator,
            rightIntMultiValue: value,
            rightFloatMultiValue: EMPTY_ARRAY,
            rightPartyIDMultiValue: EMPTY_ARRAY,
            rightStringMultiValue: EMPTY_ARRAY,
            rightTimeMultiValue: EMPTY_ARRAY,
            rightDateMultiValue: EMPTY_ARRAY,
          };
        }
        default: {
          throw new Error(
            "Invalid PropertyType.DataType for number field in getUpdateStartingConditionsInputsFromFormValues",
          );
        }
      }
    }

    // (zstanik): For all other operators, the value is expected to be a finite number.
    if (!isFiniteNumber(value)) {
      console.warn(WARNING_INVALID_FORM_VALUE, { leftFieldInstance, value });
      return undefined;
    }

    switch (leftFieldInstance.field.propertyType.dataType) {
      case "FLOAT": {
        return {
          leftFieldInstanceValueID: getFieldInstanceId(leftFieldInstance),
          operatorV2: operator,
          rightFloatValue: value,
          rightFloatMultiValue: EMPTY_ARRAY,
          rightIntMultiValue: EMPTY_ARRAY,
          rightPartyIDMultiValue: EMPTY_ARRAY,
          rightStringMultiValue: EMPTY_ARRAY,
          rightTimeMultiValue: EMPTY_ARRAY,
          rightDateMultiValue: EMPTY_ARRAY,
        };
      }
      case "INT": {
        return {
          leftFieldInstanceValueID: getFieldInstanceId(leftFieldInstance),
          operatorV2: operator,
          rightIntValue: value,
          rightFloatMultiValue: EMPTY_ARRAY,
          rightIntMultiValue: EMPTY_ARRAY,
          rightPartyIDMultiValue: EMPTY_ARRAY,
          rightStringMultiValue: EMPTY_ARRAY,
          rightTimeMultiValue: EMPTY_ARRAY,
          rightDateMultiValue: EMPTY_ARRAY,
        };
      }
      default: {
        throw new Error(
          "Invalid PropertyType.DataType for number field in getUpdateStartingConditionsInputsFromFormValues",
        );
      }
    }
  },

  // (hchen): Currently this is only used for handling the edge case when flipping the field
  // instance input type from OPTIONAL to REQUESTED (i.e. required) when the a previously OPTIONAL
  // field is set as a native field on a future task. It's not necessary to implement for field
  // types other than date and user, since we only support those as native field now.
  getUpdateFieldInstanceValueInputsFromFieldInstance: (
    _fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): UpdateFieldInstanceValueInputs[] => {
    throw new Error("not implmented yet");
  },

  getValueForFrontend: (fieldInstance: FieldInstanceFields | FieldInstanceBaseFields) => {
    return extractAtMostOneValueOrThrow({
      fieldInstance,
      fieldInstanceValueTypeName: ["FieldInstanceValueInt", "FieldInstanceValueFloat"],
      errorMessageIfMultipleValues: ERROR_INVALID_VALUE_COUNT,
      errorMessageIfWrongValueType: ERROR_INVALID_VALUE_TYPE,
      getterIfNoValue: () => null,
      getterIfValue: (fieldInstanceValue) => {
        switch (fieldInstanceValue.__typename) {
          case "FieldInstanceValueFloat": {
            const value = fieldInstanceValue.floatValue;
            if (!isFiniteNumber(value)) {
              return null;
            }
            return value;
          }
          case "FieldInstanceValueInt": {
            const value = fieldInstanceValue.intValue;
            if (!isFiniteNumber(value)) {
              return null;
            }
            return value;
          }
          default:
            assertNever(fieldInstanceValue);
        }
      },
    });
  },

  getTabularValueForFrontend: (fieldInstance: TableFieldInstanceFields) => {
    return translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance);
  },

  isCreateAndEditAllowed: true,

  isFeatureFlagEnabled: (): boolean => {
    return true;
  },

  isFieldInstanceEmpty: (fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    return frontendFieldInstance.value == null;
  },

  isFieldInstanceValueEqual: (
    fieldInstance: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
    fieldInstance2: FieldInstanceFields | FieldInstanceFieldsWithBaseValues,
  ): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    const frontendFieldInstance2 = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance2);
    return frontendFieldInstance.value === frontendFieldInstance2.value;
  },

  isFieldInstanceValueUnchanged: (fieldInstance, proposedChange): boolean => {
    const frontendFieldInstance = translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(fieldInstance);
    if (frontendFieldInstance.sourceFieldInstanceId !== proposedChange.sourceFieldInstanceValueId) {
      return false;
    }

    if (frontendFieldInstance.inputType !== proposedChange.inputType) {
      return false;
    }

    // Compare float values if we're dealing with a float-backed number field.
    if (frontendFieldInstance.valueOriginalType === "float") {
      const isStillNull = frontendFieldInstance.value == null && proposedChange.floatValue == null;
      const isSameFloatValue = frontendFieldInstance.value === proposedChange.floatValue;
      return isStillNull || isSameFloatValue;
    }

    // Compare float values if we're dealing with an int-backed number field.
    const isStillNull = frontendFieldInstance.value == null && proposedChange.intValue == null;
    const isSameIntValue = frontendFieldInstance.value === proposedChange.intValue;
    return isStillNull || isSameIntValue;
  },

  isFieldInstanceTabularValueUnchanged: (fieldInstance, proposedChange) => {
    const rows = translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance);
    const originalRow = rows.find((row) => row.rowNumber === proposedChange.rowNumber);
    if (originalRow == null) {
      return false; // This potentially is a new row
    }

    // Compare float values if we're dealing with a float-backed number field.
    if (originalRow.valueOriginalType === "float") {
      const isStillNull = originalRow.value == null && proposedChange.floatValue == null;
      const isSameFloatValue = originalRow.value === proposedChange.floatValue;
      return isStillNull || isSameFloatValue;
    }

    // Compare int values if we're dealing with an int-backed number field.
    const isStillNull = originalRow.value == null && proposedChange.intValue == null;
    const isSameIntValue = originalRow.value === proposedChange.intValue;
    return isStillNull || isSameIntValue;
  },

  renderDisplayValue: renderDisplayValue,

  renderDisplayValueFromStringValue: renderDisplayValueFromStringValue,

  renderFormField: <TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>(
    field: FieldFields,
    props: CustomFieldPluginV2RenderFormFieldProps<TFieldValues, TName>,
  ): React.ReactNode => {
    if (!canProcessField(field)) {
      throw new Error(getErrorMessageWithPayload(ERROR_INVALID_FIELD, { field }));
    }

    const fieldInstance = props.fieldInstance;
    const isDeleted = fieldInstance?.field.deletedAt != null;

    const decimalPlaces =
      (props.fieldInstance?.spectrumFieldVersion != null
        ? getDecimalPlacesFromFieldVersion(props.fieldInstance.spectrumFieldVersion)
        : undefined) ?? DEFAULT_DECIMAL_PLACES;

    const disallowGroupings =
      (props.fieldInstance?.spectrumFieldVersion != null
        ? getDisallowGroupingsFloatFromFieldVersion(props.fieldInstance.spectrumFieldVersion)
        : undefined) ?? false;

    // Merge validation rules into a single function that invokes validateValue as well as other
    // validators specific to this field type.
    const controllerRules: typeof props.controllerProps.rules = {
      ...props.controllerProps.rules,
      validate: mergeReactHookFormValidators(
        props.controllerProps.rules?.validate,
        ValidationRules.NUMBER.validate.isNumber,
      ),
    };

    return (
      <RegrelloControlledFormFieldNumber
        {...props}
        // (hchen): The keySuffix is particularly critical in the number field plugin because the
        // input is uncontrolled and doesn't respond to backing data changes.
        key={`${props.controllerProps.name}-${props.keySuffix}`}
        controllerProps={{
          ...props.controllerProps,
          rules: controllerRules,
        }}
        dataTestId={DataTestIds.CUSTOM_FIELD_VALUE_INPUT}
        formatOptions={{
          // Show exactly the number of decimal places specified by the field version.
          minimumFractionDigits: decimalPlaces != null ? decimalPlaces : undefined,
          maximumFractionDigits: decimalPlaces != null ? decimalPlaces : undefined,
          useGrouping: !disallowGroupings,
        }}
        infoTooltipText={props.description}
        isDeleted={isDeleted}
      />
    );
  },

  renderIcon: (props) => {
    return <RegrelloIcon {...props} iconName="number-field" />;
  },

  renderMultipleDisplayValuesForDataGrid: (fieldInstances, options) => {
    if (fieldInstances.length === 0) {
      return null;
    }
    if (fieldInstances.length === 1) {
      return renderDisplayValue(fieldInstances[0], { context: options?.context ?? "table" });
    }

    const instancesWithSource = fieldInstances.map((fieldInstance) => {
      return {
        content: renderDisplayValue(fieldInstance, { context: options?.context ?? "table" }),
        workflowName: fieldInstance.workflow?.name,
        stageName: fieldInstance.actionItem?.workflowReference?.stageName,
        taskName: fieldInstance.actionItem?.name,
      };
    });

    return <RegrelloCustomFieldMultiValuePopover instancesWithSource={instancesWithSource} />;
  },

  sortComparator,

  validateBulkUploadCellValue: (field, value, options) => {
    const valueAsString = value.trim();

    if (options.isRequired) {
      const result = validateRequired(valueAsString, options.isRequired);
      if (result != null) {
        return result;
      }
    }

    const valueAsWholeNumber = Number.parseInt(valueAsString, 10);
    const valueAsDecimalNumber = Number.parseFloat(valueAsString);

    if (valueAsString === EMPTY_STRING) {
      return {
        value: valueAsString,
      };
    }

    const propertyDataType = field.propertyType.dataType;

    // Number fields can be float- or int-valued (int-valued is legacy).
    switch (propertyDataType) {
      case "FLOAT": {
        if (!isFiniteNumber(valueAsDecimalNumber)) {
          return {
            value: valueAsString,
            info: [{ level: "error", message: t`Must be a valid number` }],
          };
        }

        if (valueAsDecimalNumber < Number.MIN_SAFE_INTEGER || valueAsDecimalNumber > Number.MAX_SAFE_INTEGER) {
          return {
            value: valueAsString,
            info: [{ level: "error", message: t`Number is out of range` }],
          };
        }

        return {
          value: String(valueAsDecimalNumber),
        };
      }

      case "INT": {
        if (valueAsDecimalNumber !== valueAsWholeNumber) {
          return {
            value: String(valueAsString),
            info: [{ level: "error", message: t`Must be a whole number` }],
          };
        }

        if (!isFiniteNumber(valueAsWholeNumber)) {
          return {
            value: valueAsString,
            info: [{ level: "error", message: t`Must be a valid whole number` }],
          };
        }

        if (valueAsWholeNumber < Number.MIN_SAFE_INTEGER || valueAsWholeNumber > Number.MAX_SAFE_INTEGER) {
          return {
            value: valueAsString,
            info: [{ level: "error", message: t`Number is out of range` }],
          };
        }

        return {
          value: String(valueAsWholeNumber),
        };
      }

      default: {
        throw new Error("Invalid PropertyType.DataType for number field in bulk upload");
      }
    }
  },
};

interface FrontendNumberFieldInstance {
  crossWorkflowSinksFieldInstanceIds: number[];
  crossWorkflowSourceFieldInstanceId: number | undefined;
  inputType: FieldInstanceValueInputType;
  name: string;
  sinksFieldInstanceIds: number[];
  sourceFieldInstanceId: number | undefined;
  sourceFieldInstanceInputType: FieldInstanceValueInputType | undefined;
  value: number | null;

  /**
   * The original type of value that was parsed from the field instance returned from the API. May
   * be a `float`-backend number field or a legacy `int`-backend number field.
   */
  valueOriginalType: "float" | "int";
}

function translateGraphQlFieldInstanceToFrontendFieldInstanceOrThrow(
  fieldInstance: FieldInstanceFields | FieldInstanceBaseFields,
): FrontendNumberFieldInstance {
  const { field } = fieldInstance;

  if (!canProcessField(field)) {
    throw new Error(getErrorMessageWithPayload(ERROR_INVALID_FIELD, { field }));
  }

  const isFieldInstanceFields = getIsFieldInstanceFields(fieldInstance);

  const getterIfValueForInt = (fieldInstanceValue: FieldInstanceValueFieldsFieldInstanceValueInt) => ({
    name: field.name,
    inputType: fieldInstanceValue.inputType,
    crossWorkflowSinksFieldInstanceIds:
      getIsFieldInstanceValueWithCrossWorkflowFields(fieldInstanceValue) &&
      fieldInstanceValue.crossWorkflowSinksFieldInstanceValueInt != null
        ? fieldInstanceValue.crossWorkflowSinksFieldInstanceValueInt.map((value) => value.id)
        : EMPTY_ARRAY,
    crossWorkflowSourceFieldInstanceId: getIsFieldInstanceValueWithCrossWorkflowFields(fieldInstanceValue)
      ? fieldInstanceValue.crossWorkflowSourceFieldInstanceValueInt?.id
      : undefined,
    sinksFieldInstanceIds: isFieldInstanceFields
      ? fieldInstanceValue.sinksFieldInstanceValueInt?.map(({ id }) => id)
      : EMPTY_ARRAY,
    sourceFieldInstanceId: isFieldInstanceFields ? fieldInstanceValue.sourceFieldInstanceValueInt?.id : undefined,
    sourceFieldInstanceInputType: isFieldInstanceFields
      ? fieldInstanceValue.sourceFieldInstanceValueInt?.inputType
      : undefined,
    value: isFiniteNumber(fieldInstanceValue.intValue) ? fieldInstanceValue.intValue : null,
    valueOriginalType: "int" as const,
  });

  const getterIfValueForFloat = (fieldInstanceValue: FieldInstanceValueFieldsFieldInstanceValueFloat) => ({
    name: field.name,
    inputType: fieldInstanceValue.inputType,
    crossWorkflowSinksFieldInstanceIds:
      getIsFieldInstanceValueWithCrossWorkflowFields(fieldInstanceValue) &&
      fieldInstanceValue.crossWorkflowSinksFieldInstanceValueFloat != null
        ? fieldInstanceValue.crossWorkflowSinksFieldInstanceValueFloat.map((value) => value.id)
        : EMPTY_ARRAY,
    crossWorkflowSourceFieldInstanceId: getIsFieldInstanceValueWithCrossWorkflowFields(fieldInstanceValue)
      ? fieldInstanceValue.crossWorkflowSourceFieldInstanceValueFloat?.id
      : undefined,
    sinksFieldInstanceIds: isFieldInstanceFields
      ? fieldInstanceValue.sinksFieldInstanceValueFloat?.map(({ id }) => id)
      : EMPTY_ARRAY,
    sourceFieldInstanceId: isFieldInstanceFields ? fieldInstanceValue.sourceFieldInstanceValueFloat?.id : undefined,
    sourceFieldInstanceInputType: isFieldInstanceFields
      ? fieldInstanceValue.sourceFieldInstanceValueFloat?.inputType
      : undefined,
    value: isFiniteNumber(fieldInstanceValue.floatValue) ? fieldInstanceValue.floatValue : null,
    valueOriginalType: "float" as const,
  });

  return extractAtMostOneValueOrThrow({
    fieldInstance,
    fieldInstanceValueTypeName: ["FieldInstanceValueFloat", "FieldInstanceValueInt"],
    errorMessageIfMultipleValues: ERROR_INVALID_VALUE_COUNT,
    errorMessageIfWrongValueType: ERROR_INVALID_VALUE_TYPE,
    getterIfNoValue: () => ({
      name: field.name,
      inputType: DEFAULT_INPUT_TYPE_IF_NO_VALUE,
      crossWorkflowSinksFieldInstanceIds: EMPTY_ARRAY,
      crossWorkflowSourceFieldInstanceId: undefined,
      sinksFieldInstanceIds: EMPTY_ARRAY,
      sourceFieldInstanceId: undefined,
      sourceFieldInstanceInputType: undefined,
      value: null,
      valueOriginalType: "float" as const,
    }),
    getterIfValue: (fieldInstanceValue) => {
      switch (fieldInstanceValue.__typename) {
        case "FieldInstanceValueFloat": {
          return getterIfValueForFloat(fieldInstanceValue);
        }
        case "FieldInstanceValueInt": {
          return getterIfValueForInt(fieldInstanceValue);
        }
        default:
          assertNever(fieldInstanceValue);
      }
    },
  });
}

function isOperatorValid(
  operator: ConditionOperator,
): operator is
  | "EMPTY"
  | "NOT_EMPTY"
  | "GREATER_THAN_OR_EQUALS"
  | "LESS_THAN_OR_EQUALS"
  | "EQUALS"
  | "NOT_EQUALS"
  | "BETWEEN" {
  return getConditionOperators().find((stageStartOperator) => stageStartOperator.operator === operator) != null;
}

/** Type guard that invokes Number.isFinite *and* narrows the type to number. */
function isFiniteNumber(value: unknown): value is number {
  return Number.isFinite(value);
}

function translateGraphQlFieldInstanceToTabularFrontendFieldInstanceOrThrow(fieldInstance: TableFieldInstanceFields) {
  return extractAtMostOneTabularArrayOrThrow({
    fieldInstance,

    fieldInstanceValueTypeName: ["FieldInstanceValueInt", "FieldInstanceValueFloat"],
    errorMessageIfMultipleValues: ERROR_INVALID_VALUE_COUNT,
    errorMessageIfWrongValueType: ERROR_INVALID_VALUE_TYPE,
    getterIfNoValue: () => EMPTY_ARRAY,
    getterIfTabularValue: (fieldInstanceValue) => {
      switch (fieldInstanceValue.__typename) {
        case "FieldInstanceValueFloat": {
          const values = fieldInstanceValue.tabularValueFloat?.map((value) => ({
            value: isFiniteNumber(value.value) ? value.value : null,
            rowNumber: value.rowNumber,
            valueOriginalType: "float" as const,
          }));
          return values ?? EMPTY_ARRAY;
        }
        case "FieldInstanceValueInt": {
          const values = fieldInstanceValue.tabularValueInt?.map((value) => ({
            value: isFiniteNumber(value.value) ? value.value : null,
            rowNumber: value.rowNumber,
            valueOriginalType: "int" as const,
          }));
          return values ?? EMPTY_ARRAY;
        }
        default:
          assertNever(fieldInstanceValue);
      }
    },
  });
}
